/**
 * 站内导航 chokepoint：相对路径先补静态导出尾斜杠。
 *
 * Callers: 所有需要 router.push/replace 的客户端模块
 *
 * Modules:
 * - useAppRouter — 包装 react-router useNavigate
 */

import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router";

import { toAppNavigationHref } from "@/config/normalize-application-pathname";

type NavigateOptions = {
  replace?: boolean;
  state?: unknown;
  /** Accepted for Next.js API compatibility; ignored in the SPA. */
  scroll?: boolean;
};

/** 站内 push/replace/prefetch 自动走 toStaticExportHref；绝对 URL 原样转发。 */
export function useAppRouter() {
  const navigate = useNavigate();
  const location = useLocation();

  return useMemo(() => {
    const push = (href: string, _options?: NavigateOptions) => {
      const target = toAppNavigationHref(href);
      if (/^https?:\/\//i.test(target) || target.startsWith("//")) {
        window.location.assign(target);
        return;
      }
      void navigate(target, { replace: false, state: _options?.state });
    };
    const replace = (href: string, _options?: NavigateOptions) => {
      const target = toAppNavigationHref(href);
      if (/^https?:\/\//i.test(target) || target.startsWith("//")) {
        window.location.replace(target);
        return;
      }
      void navigate(target, { replace: true, state: _options?.state });
    };
    const prefetch = (_href: string) => {
      // SPA: no route prefetch equivalent; keep API for callers.
    };
    const refresh = () => {
      void navigate(location.pathname + location.search + location.hash, {
        replace: true,
      });
    };
    const back = () => {
      void navigate(-1);
    };
    const forward = () => {
      void navigate(1);
    };
    return { push, replace, prefetch, refresh, back, forward };
  }, [navigate, location.pathname, location.search, location.hash]);
}
